import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Payment,
  Cancel,
  Edit,
  Email,
  LocationOn,
  Receipt,
  CheckCircle,
  CalendarMonth,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useInvoice, useSendInvoice, useResendInvoice } from '../../hooks/useInvoices';
import RecordPaymentModal from '../../components/invoices/RecordPaymentModal';
import ExtendDueDateModal from '../../components/invoices/ExtendDueDateModal';
import CancelInvoiceModal from '../../components/invoices/CancelInvoiceModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { COLORS } from '@elevair/shared';

// Helper function for cancellation reason labels
const getCancellationReasonLabel = (reason?: string): string => {
  const labels: Record<string, string> = {
    client_requested: 'Client requested cancellation',
    duplicate: 'Duplicate invoice',
    incorrect_amount: 'Incorrect amount',
    service_not_delivered: 'Service not delivered',
    payment_arrangement: 'Payment arrangement changed',
    other: 'Other',
  };
  return labels[reason || ''] || 'Unknown';
};

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [extendDueDateModalOpen, setExtendDueDateModalOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { data: invoice, isLoading, error } = useInvoice(id || '');
  const sendInvoice = useSendInvoice();
  const resendInvoice = useResendInvoice();

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice?.currency || 'USD',
    }).format(cents / 100);
  };

  const statusConfig: Record<string, { color: string; label: string; bgColor: string }> = {
    draft: { color: '#6B7280', label: 'Draft', bgColor: '#F3F4F6' },
    sent: { color: '#3B82F6', label: 'Sent', bgColor: '#DBEAFE' },
    paid: { color: '#10B981', label: 'Paid', bgColor: '#D1FAE5' },
    overdue: { color: '#EF4444', label: 'Overdue', bgColor: '#FEE2E2' },
    cancelled: { color: '#9CA3AF', label: 'Cancelled', bgColor: '#F3F4F6' },
    refunded: { color: '#F59E0B', label: 'Refunded', bgColor: '#FEF3C7' },
  };

  // Determine display status (check if overdue)
  const getDisplayStatus = () => {
    if (!invoice) return 'draft';
    if (invoice.status === 'sent' && new Date(invoice.dueDate) < new Date() && invoice.amountDue > 0) {
      return 'overdue';
    }
    return invoice.status;
  };

  const displayStatus = getDisplayStatus();

  const handleSendConfirm = async () => {
    if (invoice) {
      await sendInvoice.mutateAsync(invoice._id);
    }
    setSendDialogOpen(false);
  };

  const handleResendConfirm = async () => {
    if (invoice) {
      await resendInvoice.mutateAsync({ id: invoice._id });
    }
    setResendDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          Back to Invoices
        </Button>
        <Alert severity="error">
          Invoice not found or failed to load.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/invoices')}>
          <ArrowBack />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" fontWeight={600}>
              {invoice.invoiceNumber}
            </Typography>
            <Chip
              label={statusConfig[displayStatus]?.label || displayStatus}
              sx={{
                bgcolor: statusConfig[displayStatus]?.bgColor,
                color: statusConfig[displayStatus]?.color,
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Issued {format(new Date(invoice.issueDate), 'MMMM d, yyyy')}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={1}>
          {invoice.status === 'draft' && (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => navigate(`/invoices/${invoice._id}/edit`)}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => setSendDialogOpen(true)}
              >
                Send Invoice
              </Button>
            </>
          )}

          {(invoice.status === 'sent' || displayStatus === 'overdue') && (
            <>
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={() => setResendDialogOpen(true)}
              >
                Resend
              </Button>
              <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={() => setExtendDueDateModalOpen(true)}
              >
                Extend Due Date
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<Payment />}
                onClick={() => setPaymentModalOpen(true)}
              >
                Record Payment
              </Button>
            </>
          )}

          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {/* Cancellation Info Banner - Show when invoice is cancelled */}
      {invoice.status === 'cancelled' && (
        <Card sx={{ mb: 3, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Cancel color="error" />
              <Typography variant="h6" color="error">
                Invoice Cancelled
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Cancelled On
                </Typography>
                <Typography>
                  {invoice.cancelledAt
                    ? format(new Date(invoice.cancelledAt), 'MMMM d, yyyy h:mm a')
                    : 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Reason
                </Typography>
                <Typography>
                  {getCancellationReasonLabel(invoice.cancellationReason)}
                </Typography>
              </Grid>

              {invoice.cancellationNote && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography>
                    {invoice.cancellationNote}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Invoice Details */}
        <Grid item xs={12} md={8}>
          {/* Client & Dates */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    BILL TO
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Avatar
                      sx={{
                        bgcolor: invoice.clientId?.avatarColor || COLORS.primary,
                      }}
                    >
                      {invoice.clientName?.split(' ').map((n: string) => n[0]).join('')}
                    </Avatar>
                    <Typography variant="h6">{invoice.clientName}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                    <Email fontSize="small" />
                    <Typography variant="body2">{invoice.clientEmail}</Typography>
                  </Box>
                  {invoice.clientAddress && (
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary" mt={0.5}>
                      <LocationOn fontSize="small" />
                      <Typography variant="body2">{invoice.clientAddress}</Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        ISSUE DATE
                      </Typography>
                      <Typography>{format(new Date(invoice.issueDate), 'MMMM d, yyyy')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        DUE DATE
                      </Typography>
                      <Typography
                        color={displayStatus === 'overdue' ? 'error' : 'inherit'}
                        fontWeight={displayStatus === 'overdue' ? 600 : 400}
                      >
                        {format(new Date(invoice.dueDate), 'MMMM d, yyyy')}
                        {displayStatus === 'overdue' && ' (Overdue)'}
                      </Typography>
                    </Box>
                    {invoice.paidDate && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          PAID DATE
                        </Typography>
                        <Typography color="success.main">
                          {format(new Date(invoice.paidDate), 'MMMM d, yyyy')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.lineItems.map((item, index) => (
                      <TableRow key={item._id || index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ width: 300 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography color="text.secondary">Subtotal</Typography>
                    <Typography>{formatCurrency(invoice.subtotal)}</Typography>
                  </Box>

                  {invoice.discountAmount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Discount</Typography>
                      <Typography color="error">-{formatCurrency(invoice.discountAmount)}</Typography>
                    </Box>
                  )}

                  {invoice.taxAmount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">
                        Tax ({(invoice.taxRate * 100).toFixed(1)}%)
                      </Typography>
                      <Typography>{formatCurrency(invoice.taxAmount)}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">{formatCurrency(invoice.total)}</Typography>
                  </Box>

                  {invoice.amountPaid > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Amount Paid</Typography>
                      <Typography color="success.main">-{formatCurrency(invoice.amountPaid)}</Typography>
                    </Box>
                  )}

                  {invoice.amountDue > 0 && invoice.amountDue !== invoice.total && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6" color="primary">Amount Due</Typography>
                        <Typography variant="h6" color="primary">{formatCurrency(invoice.amountDue)}</Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Notes & Terms */}
          {(invoice.notes || invoice.terms) && (
            <Card>
              <CardContent>
                {invoice.notes && (
                  <Box mb={invoice.terms ? 3 : 0}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      NOTES
                    </Typography>
                    <Typography>{invoice.notes}</Typography>
                  </Box>
                )}
                {invoice.terms && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      PAYMENT TERMS
                    </Typography>
                    <Typography color="text.secondary">{invoice.terms}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Summary & Payments */}
        <Grid item xs={12} md={4}>
          {/* Amount Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box textAlign="center" py={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {invoice.status === 'paid' ? 'AMOUNT PAID' : 'AMOUNT DUE'}
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight={600}
                  color={invoice.status === 'paid' ? 'success.main' : displayStatus === 'overdue' ? 'error.main' : 'primary'}
                >
                  {formatCurrency(invoice.status === 'paid' ? invoice.total : invoice.amountDue)}
                </Typography>
                {displayStatus === 'overdue' && invoice.daysUntilDue !== undefined && (
                  <Typography variant="body2" color="error">
                    {Math.abs(invoice.daysUntilDue)} days overdue
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment History
                </Typography>
                <List disablePadding>
                  {invoice.payments.map((payment, index) => (
                    <ListItem
                      key={payment._id || index}
                      disablePadding
                      sx={{ py: 1, borderBottom: index < invoice.payments.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatCurrency(payment.amount)}
                        secondary={
                          <>
                            {format(new Date(payment.paidAt), 'MMM d, yyyy')} • {payment.method.replace('_', ' ')}
                            {payment.reference && ` • ${payment.reference}`}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Activity */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity
              </Typography>
              <List disablePadding>
                {invoice.status === 'cancelled' && invoice.cancelledAt && (
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Cancel fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Invoice cancelled"
                      secondary={format(new Date(invoice.cancelledAt), 'MMM d, yyyy h:mm a')}
                    />
                  </ListItem>
                )}
                {invoice.sentAt && (
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Send fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Invoice sent"
                      secondary={`${format(new Date(invoice.sentAt), 'MMM d, yyyy h:mm a')} to ${invoice.sentTo}`}
                    />
                  </ListItem>
                )}
                <ListItem disablePadding sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Receipt fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Invoice created"
                    secondary={format(new Date(invoice.createdAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Record Payment Modal */}
      <RecordPaymentModal
        open={paymentModalOpen}
        invoice={invoice}
        onClose={() => setPaymentModalOpen(false)}
      />

      {/* Extend Due Date Modal */}
      <ExtendDueDateModal
        open={extendDueDateModalOpen}
        invoice={invoice}
        onClose={() => setExtendDueDateModalOpen(false)}
      />

      {/* Send Confirmation Dialog */}
      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Invoice"
        message={`Send invoice ${invoice.invoiceNumber} to ${invoice.clientEmail}?`}
        confirmText="Send Invoice"
        variant="info"
        onConfirm={handleSendConfirm}
        onCancel={() => setSendDialogOpen(false)}
        loading={sendInvoice.isPending}
      />

      {/* Resend Confirmation Dialog */}
      <ConfirmDialog
        open={resendDialogOpen}
        title="Resend Invoice"
        message={`Resend invoice ${invoice.invoiceNumber} to ${invoice.clientEmail}?`}
        confirmText="Resend Invoice"
        variant="info"
        onConfirm={handleResendConfirm}
        onCancel={() => setResendDialogOpen(false)}
        loading={resendInvoice.isPending}
      />

      {/* Cancel Invoice Modal */}
      <CancelInvoiceModal
        open={cancelDialogOpen}
        invoice={invoice}
        onClose={() => setCancelDialogOpen(false)}
        onSuccess={() => navigate('/invoices')}
      />
    </Box>
  );
}
